import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useMantineTheme, useLocalizedTime, useLocalizedDate, addMinutesToDateStr, useFeatureToggle } from 'hooks'
import { LessonsContext } from 'components'
import { Stack, Group, Box, Text, ActionIcon, Button } from 'components'
import { IconEdit, IconTrash, IconClipboardPlus } from '@tabler/icons-react'
import { LessonHeaderProps } from './interfaces'

export const LessonHeader = ({ lesson, editable, kind }: LessonHeaderProps) => {
  const theme = useMantineTheme()
  const { t, i18n } = useTranslation()
  const arcNewProjectEnabled = useFeatureToggle('arc-mosaico-project')

  const { handleEditLesson, handleRemoveLesson, handleAddActivity } = useContext(LessonsContext)
  const localizeTime = useLocalizedTime()
  const localizeDate = useLocalizedDate(i18n.language || 'pt-BR')
  const isApplication = kind === 'application'

  const renderDateTime = () => {
    if (arcNewProjectEnabled) {
      // Nova versão: mostra apenas a data
      if (isApplication && lesson?.date) {
        return (
          <Text size='lg' span fw='bold'>
            {`${localizeDate(lesson.date)} - `}
          </Text>
        )
      }
      return null
    }

    // Versão original (main): mostra data + horário
    if (isApplication && lesson && lesson?.date !== null && lesson?.time && lesson?.duration) {
      return (
        <Text size='lg' span fw='bold'>
          {`${localizeDate(lesson?.date)} (${localizeTime(lesson?.time)} - ${addMinutesToDateStr(
            lesson?.time,
            Number(lesson?.duration),
            i18n.language || 'pt-BR',
          )}) - `}
        </Text>
      )
    }
    return null
  }

  if (!arcNewProjectEnabled) {
    // August (non-Arc) layout
    return (
      <Stack mb='md' mt='md' spacing='xs' align='flex-start'>
        <Group spacing='sm' align='start'>
          <Box>
            {renderDateTime()}
            <Text size='lg' span>
              {lesson.title}
            </Text>
          </Box>
          {editable && (
            <Group spacing='sm' align='center'>
              {lesson.sessionMeetingActions.edit && (
                <ActionIcon color={theme.colors.turquoise[6]} onClick={() => handleEditLesson(lesson.id)}>
                  <IconEdit size={24} />
                </ActionIcon>
              )}
              {lesson.sessionMeetingActions.delete && (
                <ActionIcon color={theme.colors.turquoise[6]} onClick={() => handleRemoveLesson(lesson.id)}>
                  <IconTrash size={24} />
                </ActionIcon>
              )}
              {lesson.sessionMeetingActions.new && (
                <Button
                  leftIcon={<IconClipboardPlus />}
                  variant='light'
                  size='sm'
                  onClick={() => handleAddActivity(lesson.id)}
                >
                  {t('new_project_page.add_activity')}
                </Button>
              )}
            </Group>
          )}
        </Group>
      </Stack>
    )
  }

  return (
    <Box mb={12}>
      <Group spacing={8} align='center' mb={8} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
          <Text size='lg' sx={{ lineHeight: 1.2, display: 'inline-flex', alignItems: 'center' }}>
            {renderDateTime()}
            <Text size='lg' span sx={{ lineHeight: 1.2 }}>
              {lesson.title}
            </Text>
          </Text>
        </Box>
        {editable && (
          <Group spacing={8} align='center'>
            {lesson.sessionMeetingActions.edit && (
              <ActionIcon color={theme.colors.turquoise[6]} onClick={() => handleEditLesson(lesson.id)}>
                <IconEdit size={20} />
              </ActionIcon>
            )}
            {lesson.sessionMeetingActions.delete && (
              <ActionIcon color={theme.colors.turquoise[6]} onClick={() => handleRemoveLesson(lesson.id)}>
                <IconTrash size={20} />
              </ActionIcon>
            )}
            {lesson.sessionMeetingActions.new && (
              <Button
                leftIcon={<IconClipboardPlus size={16} />}
                variant='light'
                size='sm'
                onClick={() => handleAddActivity(lesson.id)}
              >
                {t('new_project_page.add_activity')}
              </Button>
            )}
          </Group>
        )}
      </Group>
    </Box>
  )
}
